<script>
import { getRequest } from "../../../Helpers/api";

export default {
  props: {
    name: {
      type: String,
      require: false,
      default: null,
    },
    label: {
      type: String,
      require: false,
      default: null,
    },
    placeholder: {
      type: String,
      require: false,
      default: "انتخاب کنید",
    },
    defaultList: {
      type: Array,
      require: false,
      default: () => [],
    },
    itemTitle: {
      type: String,
      require: false,
      default: "title",
    },
    itemValue: {
      type: String,
      require: false,
      default: "value",
    },
    itemIcon: {
      type: String,
      require: false,
      default: null,
    },
    value: {
      type: null,
      require: false,
      default: null,
    },
    mapper: {
      type: Function,
      required: false,
      default: null,
    },
    getDataUrl: {
      type: String,
      required: false,
      default: null,
    },
    searchKey: {
      type: String,
      required: false,
      default: null,
    },
    defaultParams: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    listHeader: {
      type: String,
      required: false,
      default: null,
    },
    onChangeValue: {
      type: Function,
      required: false,
      default: null,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      selectedItem: null,
      isOpen: false,
      isLoading: false,
      items: [],
      localDefaultItems: [],
      searchTerm: "",
      debouncedSearch: null,
      error: null,
    };
  },

  computed: {
    isValid() {
      return !this.error;
    },
  },

  methods: {
    handleToggleList(status) {
      const root = document.getElementsByTagName("html")[0];

      this.isOpen = status;

      if (window.innerWidth < 993) {
        if (status) {
          root.classList.add("no-scroll");
        } else {
          root.classList.remove("no-scroll");
        }
      }

      if (status) {
        this.items = [...this.localDefaultItems];

        this.$nextTick(() => {
          const inputRefName = window.innerWidth < 993 ? "listInputRef" : "inputRef";

          this.$refs[inputRefName]?.focus();
          this.$refs[inputRefName]?.select();
        });
      } else {
        this.searchTerm = this.selectedItem?.[this.itemTitle] || null;
      }
    },

    isSelectedItem(item) {
      return this.selectedItem && item[this.itemValue] === this.selectedItem[this.itemValue];
    },

    handleSelectItem(item) {
      this.error = null;

      this.selectedItem = item;

      this.searchTerm = this.selectedItem?.[this.itemTitle] || null;

      this.$emit("input", item);

      this.handleToggleList(false);

      if (this.onChangeValue) this.onChangeValue(this.selectedItem);

      this.$emit("fill-next-input");
    },

    handleCloseOnFocusOut(event) {
      if (!this.$refs.componentRef.contains(event.relatedTarget)) this.handleToggleList(false);
    },

    handleResetValue() {
      this.searchTerm = null;
      this.selectedItem = {};
    },

    handleSelectOnEnter() {
      if (this.items.length) {
        this.selectedItem = this.items[0];
        handleToggleList(false);
      }
    },

    handleOpen() {
      this.$nextTick(() => {
        this.handleToggleList(true);
      });
    },

    async handleSearch(event) {
      this.searchTerm = event?.target?.value || "";

      if (!this.searchTerm && this.localDefaultItems?.length) {
        this.items = [...this.localDefaultItems];

        return;
      }

      this.isLoading = true;

      clearTimeout(this.debouncedSearch);

      this.debouncedSearch = setTimeout(async () => {
        if (this.getDataUrl) {
          const payload = {
            url: `${this.getDataUrl}${this.searchKey ? "" : `${this.searchTerm}`}`,
            params: {
              ...this.defaultParams,
              [this.searchKey]: this.searchTerm,
            },
          };

          const response = await getRequest(payload);

          this.items = this?.mapper ? this.mapper(response) : response;

          if (!this.localDefaultItems.length && !this.searchTerm) this.localDefaultItems = [...this.items];
        } else {
          this.items = this.localDefaultItems.filter((item) =>
            item[this.itemTitle]?.toLowerCase()?.includes(this.searchTerm?.toLowerCase())
          );
        }

        this.isLoading = false;
      }, 400);
    },

    validate() {
      this.error = this.selectedItem ? null : `${this.label} را انتخاب کنید`;
      return this.isValid;
    },
  },

  watch: {
    value(newValue) {
      if (!newValue || this.selectedItem[this.itemValue] !== newValue[this.itemValue]) this.selectedItem = newValue;
    },

    defaultList: {
      handler(newValue) {
        this.items = newValue;
        this.localDefaultItems = newValue;
      },
      deep: true,
    },
  },

  mounted() {
    if (this.value) {
      this.selectedItem = this.value;
    }

    this.searchTerm = this.selectedItem?.[this.itemTitle] || "";

    if (this.defaultList?.length) {
      this.items = this.defaultList;
      this.localDefaultItems = this.defaultList;
    } else if (this.getDataUrl) {
      this.handleSearch();
    }
  },
};
</script>

<template>
  <div
    :class="['booking__item', isOpen && 'is-open']"
    ref="componentRef"
    tabindex="0"
    @focusout="handleCloseOnFocusOut"
    @keyup.esc="handleToggleList(false)"
    @keyup.enter.prevent="handleSelectOnEnter"
  >
    <div
      :class="['booking__input', error && 'is-invalid', disabled && 'is-disabled']"
      @click="!isOpen && !disabled && handleToggleList(true)"
    >
      <label class="booking__input-label"> {{ label }} </label>

      <span v-if="$scopedSlots.content" class="booking__input-value">{{ searchTerm || placeholder }}</span>

      <input
        v-else
        type="text"
        ref="inputRef"
        :value="searchTerm"
        :placeholder="placeholder"
        @input="handleSearch"
        autocomplete="off"
        :disabled="disabled"
      />
    </div>

    <label v-if="error" class="booking__item-validation">{{ error }}</label>

    <div v-if="!disabled && isOpen" class="booking__select" tabindex="-1">
      <template v-if="$scopedSlots.content">
        <slot name="content" :handleSelectItem="handleSelectItem" :isSelectedItem="isSelectedItem" />
      </template>

      <template v-else>
        <div class="booking__item-header hide-in-desktop">
          <span>{{ placeholder }}</span>

          <button class="icon-close" @click="handleToggleList(false)" />
        </div>

        <div class="hide-in-desktop p--2">
          <input
            type="text"
            ref="listInputRef"
            :value="searchTerm"
            :placeholder="placeholder"
            @input="handleSearch"
            autocomplete="off"
          />
        </div>

        <div v-if="isLoading" class="booking__select-items">
          <ul class="booking__select-items-list">
            <li class="booking__select-item" v-for="item in 5" :key="`loading-${id}-${item}`">
              <div class="booking__select-skeleton" />

              <div class="booking__select-skeleton is-lg" />
            </li>
          </ul>
        </div>

        <template v-else>
          <div class="booking__select-items custom-scroll-bar" v-if="items.length">
            <div v-if="!searchTerm && listHeader" class="booking__select-items-header">
              <span> {{ listHeader }} </span>
            </div>

            <ul class="booking__select-items-list">
              <li
                role="option"
                v-for="item in items"
                :key="item[itemValue]"
                ref="optionRef"
                :data-item-value="item[itemValue]"
                :class="[
                  'booking__select-item',
                  { 'booking__select-item--selected': isSelectedItem(item) },
                  item.cssClass ? item.cssClass : '',
                ]"
                @click="handleSelectItem(item)"
                @mousedown.prevent
              >
                <i v-if="item[itemIcon]" :class="item[itemIcon]" />

                <span>{{ item[itemTitle] }}</span>

                <label v-if="item.description">{{ item.description }}</label>
              </li>
            </ul>
          </div>

          <div class="dis-block text-red p--2" v-else>مورد یافت نشد</div>
        </template>
      </template>
    </div>
  </div>
</template>
